import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './auth/context';
import { useAuth } from './auth/useAuth';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import OAuthCallback from './pages/OAuthCallback';
import HandoffCallback from './pages/HandoffCallback';
import ResetRequest from './pages/ResetRequest';
import ResetConfirm from './pages/ResetConfirm';
import type { ReactNode } from 'react';

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-page">
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function RequireGuest({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-page">
        <p>Loading...</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RequireGuest>
            <Login />
          </RequireGuest>
        }
      />
      <Route path="/account/connect/callback" element={<OAuthCallback />} />
      <Route path="/auth/handoff" element={<HandoffCallback />} />
      <Route
        path="/forgot-password"
        element={
          <RequireGuest>
            <ResetRequest />
          </RequireGuest>
        }
      />
      <Route
        path="/reset"
        element={
          <RequireGuest>
            <ResetConfirm />
          </RequireGuest>
        }
      />
      <Route
        path="/account/*"
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      />
      <Route path="/" element={<LegacyRootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function LegacyRootRedirect() {
  const location = useLocation();
  const destination = new URLSearchParams(location.search).has('billing')
    ? `/account/billing${location.search}`
    : '/account/overview';
  return <Navigate to={destination} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
